import {
  CardPayment,
  CardPaymentRequest,
  CardRequest,
  DirectDebitDetails,
  PaymentAssistBeginResponse,
  PaymentAssistRequest,
  PaymentAssistResponse,
  PaymentSuccessResponse,
  Result
} from '../components/common/CommonTypes';
import {
  BankResponse,
  MerchantSessionKey
} from '../components/common/SinglePayment/Opayo';
import { ApiHelper } from './apiHelper';
import { SagePayAccount } from '../consts/Common';

export class PaymentApi {
  validateDirectDebit(
    directDebitDetails: DirectDebitDetails,
    token: string,
    callback: (data: Result) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/directdebit';

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      directDebitDetails,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  getMerchantSessionKey(
    token: string,
    account: SagePayAccount,
    countryCode: string,
    callback: (data: MerchantSessionKey) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/merchantkey${account}?countryCode=${countryCode}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  submitPayment(
    cardIdentifier: string,
    merchantSessionKey: string,
    payment: CardPayment,
    token: string,
    account: SagePayAccount,
    countryCode: string,
    callback: (data: PaymentSuccessResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment${account}`;

    const browserLanguage = navigator.language;
    const browserJavaEnabled = navigator.javaEnabled();
    const browserColorDepth = screen.colorDepth;
    const browserScreenHeight = screen.height;
    const browserScreenWidth = screen.width;

    const d = new Date();
    const browserTZ = d.getTimezoneOffset();

    const cardRequest: CardRequest = {
      cardIdentifier: cardIdentifier,
      merchantSessionKey: merchantSessionKey,
      cardPayment: payment,
      browserLanguage: browserLanguage,
      browserJavaEnabled: browserJavaEnabled,
      browserColorDepth: browserColorDepth.toString(),
      browserScreenHeight: browserScreenHeight.toString(),
      browserScreenWidth: browserScreenWidth.toString(),
      browserTZ: browserTZ.toString()
    };

    const data: CardPaymentRequest = {
      cardRequest: cardRequest,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  voidPayment(
    transactionId: string,
    countryCode: string,
    token: string,
    account: SagePayAccount,
    callback: () => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/void${account}`;

    const data = {
      transactionId: transactionId,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  getBankResponse(
    transactionId: string,
    token: string,
    callback: (data: BankResponse) => void,
    failureCallback: () => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/bankresponse/${transactionId}`;

    new ApiHelper().getDataSecureAsync(
      dataUrl,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  sendOrder(
    token: string,
    payment: CardPayment,
    isCallCentreUser: boolean,
    callback: (data: PaymentSuccessResponse) => void,
    failureCallback: () => void,
    countryCode: string,
    language: string,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/wp${isCallCentreUser ? '/cc' : ''}`;

    const browserJavaEnabled = navigator.javaEnabled();
    const browserColorDepth = screen.colorDepth;
    const browserScreenHeight = screen.height;
    const browserScreenWidth = screen.width;

    const d = new Date();
    const browserTZ = d.getTimezoneOffset();

    const cardRequest: CardRequest = {
      cardIdentifier: '',
      merchantSessionKey: '',
      cardPayment: payment,
      browserLanguage: language,
      browserJavaEnabled: browserJavaEnabled,
      browserColorDepth: browserColorDepth.toString(),
      browserScreenHeight: browserScreenHeight.toString(),
      browserScreenWidth: browserScreenWidth.toString(),
      browserTZ: browserTZ.toString()
    };

    const data: CardPaymentRequest = {
      cardRequest: cardRequest,
      countryCode: countryCode
    };

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  paymentResponse(
    token: string,
    orderNo: string,
    status: string,
    error: string,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/singlepayment/wp/response`;

    const data = {
      orderNo: orderNo,
      status: status,
      error: error
    };

    new ApiHelper().postDataSecureAsync(dataUrl, data, token, signal);
  }

  updateWorldPayPayment(
    token: string,
    orderNo: string,
    updateType: string,
    isCallCentreUser: boolean,
    amount: number,
    policyNumbers: string,
    customerName: string,
    callback: () => void,
    failureCallback: () => void,
    countryCode: string,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/singlepayment/wp';

    const data = {
      orderNo: orderNo,
      updateType: updateType,
      isCallCentreUser: isCallCentreUser,
      countryCode: countryCode,
      amount: amount,
      policyNumbers: policyNumbers,
      customerName: customerName
    };

    new ApiHelper().putDataSecureAsync(
      dataUrl,
      data,
      token,
      callback,
      failureCallback,
      signal
    );
  }

  beginPaymentAssistRequest(
    paymentAssistRequest: PaymentAssistRequest,
    token: string,
    successCallback: (data: PaymentAssistBeginResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = '/api/payment/paymentassist';

    new ApiHelper().postDataSecureAsync(
      dataUrl,
      paymentAssistRequest,
      token,
      successCallback,
      failureCallback,
      signal
    );
  }

  getPaymentAssistState(
    id: string,
    successCallback: (response: PaymentAssistResponse) => void,
    failureCallback: (error: string) => void,
    signal: AbortSignal
  ): void {
    const dataUrl = `/api/payment/paymentassist/transactions/${id}`;

    new ApiHelper().getDataAsync(
      dataUrl,
      successCallback,
      failureCallback,
      signal
    );
  }
}
